// src/Pages/DashboardAssistant/DashboardLibraryPage.jsx
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MsalAuthenticationTemplate, useMsal } from '@azure/msal-react';
import { InteractionType } from '@azure/msal-browser';
import { loginRequest } from '../../authConfig';
import { PageLayout2 } from '../../Components/PageLayout/PageLayout2';
import { DashboardAssistantDataService } from '../../Services/DashboardAssistantDataService';
import DashboardCanvasFlexible from './DashboardCanvasFlexible';
import ConfirmationModal from '../../Components/ConfirmationModal/ConfirmationModal';
import './DashboardLibrary.scss';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faArrowLeftRotate, faArrowRight, faEllipsis } from '@fortawesome/free-solid-svg-icons';

const formatDate = (value) => {
  if (!value) return '-';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
};

const parseLayout = (value) => {
  if (!value) return { width: 816, height: 1056 }; // Letter portrait
  try {
    const parsed = typeof value === 'string' ? JSON.parse(value) : value;
    if (parsed && typeof parsed === 'object') {
      if (Number.isFinite(parsed.width) || Number.isFinite(parsed.height)) {
        return {
          width: Number(parsed.width) || 816,
          height: Number(parsed.height) || 1056,
        };
      }
    }
    return { width: 816, height: 1056 };
  } catch (_) {
    return { width: 816, height: 1056 };
  }
};

const normalizeSummary = (summary) => ({
  id: summary.dashboardId ?? summary.DashboardId,
  name: summary.name ?? summary.Name ?? 'Untitled dashboard',
  lastUpdated: summary.lastUpdatedDate ?? summary.LastUpdatedDate
});

const normalizeTiles = (tiles = []) =>
  tiles.map((tile, index) => {
    let pos = tile.positionJson ?? tile.PositionJson ?? null;
    if (typeof pos === 'string') {
      try {
        pos = JSON.parse(pos);
      } catch (_) {
        pos = null;
      }
    }
    return {
      key: tile.tileId ?? tile.TileId ?? `tile-${index}`,
      tileId: tile.tileId ?? tile.TileId ?? null,
      title: tile.title ?? tile.Title ?? '',
      description: tile.description ?? tile.Description ?? '',
      sqlQuery: tile.sqlQuery ?? tile.SqlQuery ?? '',
      chartSpec: tile.chartSpec ?? tile.ChartSpec ?? {},
      data: tile.data ?? tile.Data ?? [],
      columns: tile.columns ?? tile.Columns ?? [],
      lastRunDate: tile.lastRunDate ?? tile.LastRunDate,
      tileType: tile.tileType ?? tile.TileType ?? 'chart',
      textContent: tile.textContent ?? tile.TextContent ?? '',
      imageUrl: tile.imageUrl ?? tile.ImageUrl ?? '',
      positionJson: pos ? JSON.stringify(pos) : JSON.stringify({ x: 0, y: 0, width: 320, height: 240 }),
      displayOrder: tile.displayOrder ?? tile.DisplayOrder ?? index
    };
  });

const normalizeViewTiles = (tiles = []) =>
  tiles.map((tile, index) => {
    let pos = tile.positionJson ?? tile.PositionJson ?? null;
    if (typeof pos === 'string') {
      try {
        pos = JSON.parse(pos);
      } catch (_) {
        pos = null;
      }
    }
    return {
      id: tile.tileId ?? tile.TileId ?? tile.id ?? tile.Id ?? `tile-${index}`,
      title: tile.title ?? tile.Title ?? '',
      description: tile.description ?? tile.Description ?? '',
      chartSpec: tile.chartSpec ?? tile.ChartSpec ?? {},
      data: tile.data ?? tile.Data ?? [],
      columns: tile.columns ?? tile.Columns ?? [],
      lastRunDate: tile.lastRunDate ?? tile.LastRunDate,
      tileType: tile.tileType ?? tile.TileType ?? 'chart',
      textContent: tile.textContent ?? tile.TextContent ?? '',
      imageUrl: tile.imageUrl ?? tile.ImageUrl ?? '',
      showTitle: tile.showTitle ?? tile.ShowTitle ?? true,
      showLegend: tile.showLegend ?? tile.ShowLegend ?? true,
      showAxis: tile.showAxis ?? tile.ShowAxis ?? true,
      showGrid: tile.showGrid ?? tile.ShowGrid ?? true,
      position: pos ?? { x: 20 + (index % 3) * 360, y: 20 + Math.floor(index / 3) * 280, width: 320, height: 240 }
    };
  });

const normalizeDetails = (details) => ({
  dashboardId: details.dashboardId ?? details.DashboardId,
  name: details.name ?? details.Name ?? 'Untitled dashboard',
  layoutJson: details.layoutJson ?? details.LayoutJson ?? '{}',
  createdDate: details.createdDate ?? details.CreatedDate,
  lastUpdatedDate: details.lastUpdatedDate ?? details.LastUpdatedDate,
  tiles: normalizeTiles(details.tiles ?? details.Tiles ?? [])
});

const DashboardLibraryContent = () => {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const account = accounts?.[0];
  const [collapsed, setCollapsed] = useState(false); // ADDED
  const [summaries, setSummaries] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [details, setDetails] = useState(null);
  const [layoutJson, setLayoutJson] = useState('{}');
  const [editableName, setEditableName] = useState('');
  const [editableTiles, setEditableTiles] = useState([]);
  const [viewLayout, setViewLayout] = useState({ template: '2x2' });
  const [viewTiles, setViewTiles] = useState([]);
  const [loading, setLoading] = useState({ list: false, details: false, delete: false, view: false });
  const [statusMessage, setStatusMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const getToken = async () => {
    if (!account) throw new Error('No MSAL account available');
    const request = { ...loginRequest, account };
    const res = await instance.acquireTokenSilent(request);
    return res.accessToken;
  };

  const refreshSummaries = async () => {
    setLoading((prev) => ({ ...prev, list: true }));
    setErrorMessage('');
    try {
      const token = await getToken();
      const response = await DashboardAssistantDataService.list(token);
      const normalized = (response ?? []).map(normalizeSummary);
      setSummaries(normalized);
      if (!selectedId && normalized.length) {
        setSelectedId(normalized[0].id);
      } else if (selectedId && !normalized.some((s) => s.id === selectedId)) {
        setSelectedId(normalized[0]?.id ?? null);
      }
    } catch (err) {
      console.error(err);
      setSummaries([]);
      setErrorMessage('Unable to load saved dashboards right now.');
    } finally {
      setLoading((prev) => ({ ...prev, list: false }));
    }
  };

  const refreshDetails = async (dashboardId) => {
    if (!dashboardId) {
      setDetails(null);
      return;
    }
    setLoading((prev) => ({ ...prev, details: true }));
    setErrorMessage('');
    try {
      const token = await getToken();
      const response = await DashboardAssistantDataService.details(dashboardId, token);
      setDetails(normalizeDetails(response));
    } catch (err) {
      console.error(err);
      setDetails(null);
      setErrorMessage('Unable to load dashboard details.');
    } finally {
      setLoading((prev) => ({ ...prev, details: false }));
    }
  };

  const refreshDashboardView = async (dashboardId, { reload } = {}) => {
    if (!dashboardId) {
      setViewTiles([]);
      return;
    }
    setLoading((prev) => ({ ...prev, view: true }));
    setErrorMessage('');
    try {
      const token = await getToken();
      const response = reload
        ? await DashboardAssistantDataService.reload(dashboardId, token)
        : await DashboardAssistantDataService.load(dashboardId, token);
      const tiles = normalizeViewTiles(response?.tiles ?? response?.Tiles ?? []);
      const layout = parseLayout(response?.layoutJson ?? response?.LayoutJson ?? layoutJson);
      setViewTiles(tiles);
      setViewLayout(layout);
    } catch (err) {
      console.error(err);
      setViewTiles([]);
      setErrorMessage('Unable to load dashboard tiles.');
    } finally {
      setLoading((prev) => ({ ...prev, view: false }));
    }
  };

  useEffect(() => {
    if (account) {
      refreshSummaries();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account]);

  useEffect(() => {
    if (selectedId) {
      refreshDetails(selectedId);
      refreshDashboardView(selectedId);
    } else {
      setDetails(null);
      setViewTiles([]);
    }
  }, [selectedId]);

  useEffect(() => {
    if (details) {
      setEditableName(details.name);
      setLayoutJson(details.layoutJson);
      setEditableTiles(details.tiles);
      setViewLayout(parseLayout(details.layoutJson));
    } else {
      setEditableName('');
      setLayoutJson('{}');
      setEditableTiles([]);
      setViewLayout({ template: '2x2' });
    }
  }, [details]);

  const handleTileChange = (key, field, value) => {
    setEditableTiles((prev) =>
      prev.map((tile) => (tile.key === key ? { ...tile, [field]: value } : tile))
    );
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    setLoading((prev) => ({ ...prev, delete: true }));
    setErrorMessage('');
    setStatusMessage('');
    try {
      const token = await getToken();
      const success = await DashboardAssistantDataService.delete(selectedId, token);
      if (success) {
        setStatusMessage('Dashboard deleted.');
        await refreshSummaries();
      } else {
        setErrorMessage('Unable to delete the selected dashboard.');
      }
    } catch (err) {
      console.error(err);
      setErrorMessage('Delete request failed.');
    } finally {
      setShowDeleteModal(false);
      setLoading((prev) => ({ ...prev, delete: false }));
    }
  };

  const handleEdit = () => {
    if (!selectedId) return;
    navigate(`/dashboard-assistant?dashboardId=${selectedId}`, {
      state: {
        name: editableName,
        layoutJson,
        tiles: editableTiles
      }
    });
  };

  const handleNew = () => {
    navigate('/dashboard-assistant');
  }

  const selectedSummary = useMemo(() => summaries.find((s) => s.id === selectedId), [summaries, selectedId]);

  return (
    <div className={`dashboard-library ${collapsed ? 'collapsed' : ''}`}>
      <section className="library-panel library-list-panel">
        <div className="library-panel-header">
          <button
            className="btn btn-light btn-md collapse-btn"
            onClick={() => setCollapsed(!collapsed)}
          >
            {collapsed ? 
              <FontAwesomeIcon icon={faArrowRight} /> : 
              <FontAwesomeIcon icon={faArrowLeft} />
            }
          </button>
          <button className="btn btn-outline-primary btn-sm" onClick={refreshSummaries} disabled={loading.list}>
            {loading.list ? 
              <FontAwesomeIcon icon={faEllipsis} /> :
              <FontAwesomeIcon icon={faArrowLeftRotate} />
            }
          </button>
        </div>
        <div className="library-summary-list">
          {summaries.length ? (
            summaries.map((summary) => (
              <button
                key={summary.id}
                type="button"
                className={`library-summary-item ${selectedId === summary.id ? 'active' : ''}`}
                onClick={() => setSelectedId(summary.id)}
              >
                <div className="summary-title">{summary.name}</div>
                <div className="summary-meta">{formatDate(summary.lastUpdated)}</div>
              </button>
            ))
          ) : (
            <p className="empty-state">{loading.list ? 'Loading dashboards...' : 'No dashboards were found.'}</p>
          )}
        </div>
      </section>

      <section className="library-panel library-details-panel">
        <div className="library-panel-header">
          <div>
            <h3>{selectedSummary?.name ?? 'Select a dashboard'}</h3>
          </div>
          <div className="library-actions">
            <button
              className="btn btn-primary btn-sm"
              onClick={handleNew}
            >
              Create New
            </button>
            <button
              className="btn btn-outline-primary btn-sm"
              onClick={() => {
                refreshDetails(selectedId);
                refreshDashboardView(selectedId, { reload: true });
              }}
              disabled={!selectedId || loading.details || loading.view}
            >
              {loading.details || loading.view ? 'Refreshing...' : 'Refresh'}
            </button>
            <button
              className="btn btn-outline-success btn-sm"
              onClick={handleEdit}
              disabled={!details}
            >
              Edit
            </button>
            <button
              className="btn btn-outline-danger btn-sm"
              onClick={() => setShowDeleteModal(true)}
              disabled={!details || loading.delete}
            >
              {loading.delete ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>

        {errorMessage && <div className="alert alert-danger py-1">{errorMessage}</div>}
        {statusMessage && <div className="alert alert-success py-1">{statusMessage}</div>}

        {details ? (
          <div className="library-details-body">
            <div className="library-details-meta">
              <span>Created {formatDate(details.createdDate)}</span>
              <span>Last updated {formatDate(details.lastUpdatedDate)}</span>
            </div>
            <div className="library-canvas-wrapper">
              {loading.view ? (
                <p className="empty-state">Loading dashboard...</p>
              ) : viewTiles.length ? (
                <DashboardCanvasFlexible
                  tiles={viewTiles}
                  layout={viewLayout}
                  readOnly
                />
              ) : (
                <p className="empty-state">No tiles to display for this dashboard.</p>
              )}
            </div>
          </div>
        ) : (
          <p className="empty-state">Select a dashboard to render it.</p>
        )}
      </section>

      <ConfirmationModal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        title="Delete dashboard"
        content={`Are you sure you want to delete "${selectedSummary?.name ?? 'this dashboard'}"? This action cannot be undone.`}
        onAcceptClick={handleDelete}
        onCancelClick={() => setShowDeleteModal(false)}
      />
    </div>
  );
};

const DashboardLibraryPage = ({ title = 'Smart Dashboards Library' }) => (
  <MsalAuthenticationTemplate interactionType={InteractionType.Redirect} authenticationRequest={loginRequest}>
    <PageLayout2 title={title}>
      <DashboardLibraryContent />
    </PageLayout2>
  </MsalAuthenticationTemplate>
);

export default DashboardLibraryPage;
